import { useMemo, useCallback } from 'react';

/**
 * useFreezePane - Hook for frozen header and left columns in tables
 * 
 * Provides CSS styles for:
 * - Sticky header row (frozen at top)
 * - Sticky left columns (frozen at left)
 * - Proper z-index stacking for overlap areas
 * 
 * @param {Object} options
 * @param {number} options.frozenColumnCount - Number of left columns to freeze (default: 0)
 * @param {Array} options.frozenColumnWidths - Width of each frozen column [40, 60, 100]
 * @param {string} options.headerBgColor - Header background color
 * @param {string} options.frozenBgColor - Frozen column background color (for alternating rows)
 * @param {number} options.headerHeight - Header row height for positioning
 * @returns {Object} Styles and helpers for frozen panes
 */
const useFreezePane = ({
  frozenColumnCount = 0,
  frozenColumnWidths = [],
  headerBgColor = '#f8f9fa',
  frozenBgColor = '#ffffff',
  headerHeight = 40,
} = {}) => {

  // Calculate cumulative left positions for each frozen column
  const frozenLeftPositions = useMemo(() => {
    const positions = [0];
    for (let i = 0; i < frozenColumnWidths.length; i++) {
      positions.push(positions[i] + frozenColumnWidths[i]);
    }
    return positions;
  }, [frozenColumnWidths]);

  // Total width of all frozen columns
  const totalFrozenWidth = useMemo(() => {
    return frozenColumnWidths.reduce((sum, w) => sum + w, 0);
  }, [frozenColumnWidths]);

  /**
   * Get styles for a header cell
   * @param {number} columnIndex - Column index (0-based)
   * @param {Object} baseStyle - Base styles to extend
   */
  const getHeaderCellStyle = useCallback((columnIndex, baseStyle = {}) => {
    const isFrozen = columnIndex < frozenColumnCount;
    
    const style = {
      position: 'sticky',
      top: 0,
      zIndex: isFrozen ? 12 : 10, // Frozen header cells have higher z-index
      // Only use headerBgColor if no backgroundColor provided in baseStyle
      backgroundColor: baseStyle.backgroundColor || headerBgColor,
      ...baseStyle,
    };

    if (isFrozen) {
      style.left = frozenLeftPositions[columnIndex];
      style.zIndex = 12; // Highest z-index for frozen header cells
    }

    return style;
  }, [frozenColumnCount, frozenLeftPositions, headerBgColor]);

  /**
   * Get styles for a body cell
   * @param {number} columnIndex - Column index (0-based)
   * @param {number} rowIndex - Row index for alternating colors
   * @param {Object} baseStyle - Base styles to extend
   * @param {boolean} isSelected - Whether row is selected
   */
  const getBodyCellStyle = useCallback((columnIndex, rowIndex = 0, baseStyle = {}, isSelected = false) => {
    const isFrozen = columnIndex < frozenColumnCount;
    
    if (!isFrozen) {
      return baseStyle;
    }

    // Determine background color for frozen cells
    const bgColor = isSelected 
      ? '#e3f2fd' 
      : (rowIndex % 2 === 0 ? frozenBgColor : '#f8f9fa');

    return {
      ...baseStyle,
      position: 'sticky',
      left: frozenLeftPositions[columnIndex],
      zIndex: 5,
      backgroundColor: bgColor,
    };
  }, [frozenColumnCount, frozenLeftPositions, frozenBgColor]);

  /**
   * Get container styles for the scrollable table wrapper
   */
  const containerStyle = useMemo(() => ({
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
  }), []);

  /**
   * Get table styles
   */
  const tableStyle = useMemo(() => ({
    borderCollapse: 'separate',
    borderSpacing: 0,
  }), []);

  /**
   * Get thead styles
   */
  const theadStyle = useMemo(() => ({
    position: 'sticky',
    top: 0,
    zIndex: 10,
  }), []);

  /**
   * Check if a column is frozen
   */
  const isColumnFrozen = useCallback((columnIndex) => {
    return columnIndex < frozenColumnCount;
  }, [frozenColumnCount]);

  return {
    // Style getters
    getHeaderCellStyle,
    getBodyCellStyle,
    containerStyle,
    tableStyle,
    theadStyle,
    
    // Computed values
    frozenLeftPositions,
    totalFrozenWidth,
    frozenColumnCount,
    
    // Helpers
    isColumnFrozen,
  };
};

export default useFreezePane;
